'use client';

import { SocialLinks } from '@/types';

const ICONS: Record<string, { icon: string; label: string }> = {
  twitter:   { icon: 'close',       label: 'Twitter' }, // Material Symbols don't ship a good Twitter glyph — using X-style close cross
  x:         { icon: 'close',       label: 'X' },
  github:    { icon: 'code',        label: 'GitHub' },
  linkedin:  { icon: 'work',        label: 'LinkedIn' },
  instagram: { icon: 'photo_camera',label: 'Instagram' },
  website:   { icon: 'language',    label: 'Website' },
  youtube:   { icon: 'play_circle', label: 'YouTube' },
  tiktok:    { icon: 'music_note',  label: 'TikTok' },
};

/**
 * Renders only the socials the user has filled in. Order is stable across keys.
 *
 * Security: `social_links` is a free-text JSON column on the user model — any
 * signed-in user can write whatever string they like. We must reject anything
 * that isn't a plain http/https URL before piping it into `<a href>`. Without
 * this filter, a value like `javascript:fetch('/api/...')` would XSS every
 * visitor who clicks the icon, in our origin, with their auth cookies. Same
 * class of bug as `social.*_url` from admin config — we use the same fix:
 * resolve to a safe URL or render a no-op `#` href.
 *
 * Items whose URL doesn't resolve to a safe http/https URL are dropped from
 * the rendered list entirely (no broken icon, no clickable trap).
 */
export default function ProfileSocials({ links }: { links: SocialLinks }) {
  const entries = (Object.entries(links) as [keyof SocialLinks, string][])
    .map(([key, raw]) => [key, raw, normalizeHref(raw)] as const)
    .filter(([, raw, href]) => raw && raw.trim() && href !== null)
    .sort((a, b) => a[0].localeCompare(b[0]));
  if (entries.length === 0) return null;

  return (
    <div className="profile-socials">
      {entries.map(([key, raw, href]) => {
        const cfg = ICONS[key as string] ?? { icon: 'link', label: key };
        const host = niceHostLabel(href!) ?? cfg.label;
        return (
          <a
            key={key}
            href={href!}
            target="_blank"
            rel="noopener noreferrer nofollow ugc"
            className="profile-social"
            title={`${cfg.label}: ${raw}`}
          >
            <span className="material-symbols-outlined">{cfg.icon}</span>
            <span>{host}</span>
          </a>
        );
      })}
    </div>
  );
}

/**
 * Convert a free-text user input into a safe `https://...` URL, or null if
 * we can't. Strict scheme allowlist — `javascript:`, `data:`, `file:`, etc.
 * are dropped. Any value containing a control character (CR/LF/Tab) is also
 * rejected.
 *
 * Accepted shapes (all resolve to https):
 *   1. `https://example.com/foo`         → unchanged
 *   2. `http://example.com/foo`          → unchanged
 *   3. `//example.com/foo`               → `https://example.com/foo`
 *   4. `@handle`                         → `https://twitter.com/handle`
 *   5. `example.com/foo` or `example.com`→ `https://example.com/foo`
 *
 * Anything else returns null (icon hidden).
 */
function normalizeHref(input: string): string | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (!s) return null;
  // No control characters anywhere — guards against header / display poisoning.
  if (/[\r\n\t]/.test(s)) return null;
  if (s.length > 2048) return null;

  let candidate: string;
  if (/^https?:\/\//i.test(s)) {
    candidate = s;
  } else if (s.startsWith('//')) {
    candidate = `https:${s}`;
  } else if (/^@[A-Za-z0-9_]{1,30}$/.test(s)) {
    // Bare twitter handle: `@username` — alphanumeric/underscore only.
    candidate = `https://twitter.com/${s.slice(1)}`;
  } else if (/^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+(\/.*)?$/.test(s)) {
    // Bare domain like `example.com/path` — ASCII chars only, at least one dot.
    candidate = `https://${s}`;
  } else {
    return null;
  }

  // Final WHATWG URL check — if it doesn't parse, or the parsed scheme isn't
  // http/https, refuse. Belt-and-braces against any clever bypass that slips
  // past the regex checks above.
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function niceHostLabel(safeHref: string): string | null {
  try {
    const url = new URL(safeHref);
    return url.host.replace(/^www\./, '');
  } catch {
    return null;
  }
}
